import {
  BellAlertIcon,
  ClockIcon,
  Cog8ToothIcon,
  ComputerDesktopIcon,
  CubeIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  UsersIcon,
  ReceiptRefundIcon,
  DeviceTabletIcon,
  QrCodeIcon,
  DocumentIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  BoltIcon,
  MegaphoneIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline";
import { Revenue } from "./definitions";
import { DateTime } from "luxon";
import moment from "moment";
import momenttz from 'moment-timezone';
import Papa from 'papaparse';
import * as XLSX from "xlsx";
import { setSubscriptionData } from "./indexedDB";
import { ROLE_RANGES, VALID_PROVIDER_TYPES, PROVIDER_TYPES, INTEGRATIONS_TYPE, INTEGRATION_CREDENTIAL_FIELDS } from "./constants";
import { getWeek, getWeekYear, startOfWeek, endOfWeek, subWeeks, format, subMonths, startOfMonth, endOfMonth, startOfDay, parseISO, lastDayOfMonth, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";

/**
 * Determines if a role ID belongs to a provider role
 * @param roleId The role ID to check
 * @returns Boolean indicating if the role is a provider role
 */
export function isProviderRole(roleId: string | null | undefined): boolean {
  if (!roleId) return false;
  
  const roleIdNum = parseInt(roleId);
  return !isNaN(roleIdNum) && 
    roleIdNum >= ROLE_RANGES.PROVIDER.MIN && 
    roleIdNum <= ROLE_RANGES.PROVIDER.MAX;
}

/**
 * Determines if a provider type is valid
 * @param providerType The provider type to check
 * @returns Boolean indicating if the provider type is valid
 */
export function isProviderType(providerType: string | null | undefined): boolean {
  if (!providerType) return false;
  return VALID_PROVIDER_TYPES.includes(providerType);
}

/**
 * Determines if a user is a valid provider by checking both role and provider type
 * @param roleId The role ID to check
 * @param providerType The provider type to check
 * @returns Boolean indicating if the user is a valid provider
 */
export function isValidProvider(
  roleId: string | null | undefined,
  providerType: string | null | undefined
): boolean {
  return isProviderType(providerType) && isProviderRole(roleId);
}

/**
 * Determines if a user is a specific type of provider with proper range validation
 * First checks general provider validation, then validates the specific type and range
 * @param roleId The role ID to check
 * @param providerType The provider type to check
 * @param expectedProviderType The specific provider type to validate against
 * @returns Boolean indicating if the user is the expected provider type
 */
export function isSpecificProviderType(
  roleId: string | null | undefined,
  providerType: string | null | undefined,
  expectedProviderType: string
): boolean {
  // First check general provider validation
  if (!isValidProvider(roleId, providerType)) {
    return false;
  }

  // Then check if it matches the expected specific type
  if (providerType !== expectedProviderType) {
    return false;
  }

  // Check if roleId is provided
  if (!roleId) {
    return false;
  }

  // Finally check if the role is in the correct range for this provider type
  const roleIdNum = parseInt(roleId);
  if (isNaN(roleIdNum)) {
    return false;
  }

  const range = getProviderRoleRange(providerType, roleIdNum);
  return roleIdNum >= range.min && roleIdNum <= range.max;
}

/**
 * Get provider role range based on provider type and role ID
 * First checks if providerType matches roleId range
 * Falls back to general PROVIDER range if no specific match
 */
export function getProviderRoleRange(
  providerType: string | null | undefined,
  roleId: number
): { min: number; max: number } {
  if (providerType) {
    const providerTypeEntry = Object.entries(PROVIDER_TYPES).find(
      ([, value]) => value === providerType
    );

    if (providerTypeEntry) {
      const [typeKey] = providerTypeEntry;
      const rangeKey = `PROVIDER_${typeKey}` as keyof typeof ROLE_RANGES;
      const range = ROLE_RANGES[rangeKey];

      if (
        range &&
        roleId >= range.MIN &&
        roleId <= range.MAX
      ) {
        return { min: range.MIN, max: range.MAX };
      }
    }
  }

  // Fallback: Return general PROVIDER range
  return { min: ROLE_RANGES.PROVIDER.MIN, max: ROLE_RANGES.PROVIDER.MAX };
}


/**
 * Derive selected organization info (name, shortCode) from context options.
 */
export function getSelectedOrganizationInfo(
  selectedDspOrganizationId: string | number | null | undefined,
  serviceAreaOptions: Array<{
    organizationId?: string | number | null;
    organizationName?: string | null;
    shortCode?: string | null;
    value?: string;
  }> | null | undefined
): { name: string | null; shortCode: string | null } {
  try {
    if (!selectedDspOrganizationId || !serviceAreaOptions || serviceAreaOptions.length === 0) {
      return { name: null, shortCode: null };
    }

    const orgOption = serviceAreaOptions.find((opt: any) => 
      opt && opt.organizationId == selectedDspOrganizationId && opt.value !== 'All'
    );

    return {
      name: orgOption?.organizationName || null,
      shortCode: orgOption?.shortCode || null,
    };
  } catch (e) {
    return { name: null, shortCode: null };
  }
}

export const convertTo12HourFormat = (time24: string) => {
  const [hour, minute] = time24.split(":").map(Number);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12; // Converts 0 to 12 for AM
  return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
};

export const formatDateByTimezone = (date: any, timeZone: string, format: string) => {
  const formattedDate = momenttz
    .tz(
      moment(date).format(format),
      timeZone
    )
    .format(format);

  return formattedDate;
}

export const formatText = (text: any) => {
  if (text)
    return text
      .toLowerCase()
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char: any) => char.toUpperCase())// Capitalize first letter of each word
      .replace(' Us', ' US')
  else
    return text;
};

export const formatCurrency = (amount: number) => {
  return (amount / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

export const formatDateToLocal = (
  dateStr: string,
  locale: string = "en-US"
) => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(date);
};

export const generateYAxis = (revenue: Revenue[]) => {
  // Calculate what labels we need to display on the y-axis
  // based on highest record and in 1000s
  const yAxisLabels = [];
  const highestRecord = Math.max(...revenue.map((month) => month.revenue));
  const topLabel = Math.ceil(highestRecord / 1000) * 1000;

  for (let i = topLabel; i >= 0; i -= 1000) {
    yAxisLabels.push(`$${i / 1000}K`);
  }

  return { yAxisLabels, topLabel };
};

export const generatePagination = (currentPage: number, totalPages: number) => {
  // If the total number of pages is 7 or less,
  // display all pages without any ellipsis.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // If the current page is among the first 3 pages,
  // show the first 3, an ellipsis, and the last 2 pages.
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages - 1, totalPages];
  }

  // If the current page is among the last 3 pages,
  // show the first 2, an ellipsis, and the last 3 pages.
  if (currentPage >= totalPages - 2) {
    return [1, 2, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  // If the current page is somewhere in the middle,
  // show the first page, an ellipsis, the current page and its neighbors,
  // another ellipsis, and the last page.
  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

export function formatEpochToDateTime(epochMs: number, timezone: string = 'UTC'): string {
  const date = new Date(epochMs);
  
  const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
  };
  
  return date.toLocaleString('en-US', options);
}

export const convertEpochTimeAsPerTimeZone = (
  epochTime: string | number,
  timeZone: string
) => {
  // Parse epochTime as number
  const epochNum = typeof epochTime === "string" ? parseFloat(epochTime) : epochTime;

  // Determine if epochNum is in seconds (10 digits or less) or milliseconds (13 digits or more)
  // If in seconds, convert to milliseconds by multiplying by 1000
  const epochTimeMs = epochNum < 1e12 ? epochNum * 1000 : epochNum;

  // Create a Date object
  const date = new Date(epochTimeMs);

  // Specify the time zone and include date and time components
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
  };

  // Format the date
  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
  return formattedDate;
};

/**
 * Converts epoch time to full datetime string in the specified timezone
 * Returns format: "YYYY-MM-DD HH:MM AM/PM"
 */
export const convertEpochToFullDateTime = (
  epochTime: string | number,
  timeZone: string
) => {
  // Parse epochTime as number
  const epochNum = typeof epochTime === "string" ? parseFloat(epochTime) : epochTime;

  // Determine if epochNum is in seconds (10 digits or less) or milliseconds (13 digits or more)
  // If in seconds, convert to milliseconds by multiplying by 1000
  const epochTimeMs = epochNum < 1e12 ? epochNum * 1000 : epochNum;

  // Create a Date object
  const date = new Date(epochTimeMs);

  // Format with date and time components
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  // Format the full datetime
  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);
  
  // Extract parts and reconstruct as YYYY-MM-DD HH:MM AM/PM
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  const hour = parts.find(p => p.type === 'hour')?.value;
  const minute = parts.find(p => p.type === 'minute')?.value;
  const period = parts.find(p => p.type === 'dayPeriod')?.value;

  return `${year}-${month}-${day} ${hour}:${minute} ${period}`;
};


// Function specifically for converting milliseconds to time format
export const convertMillisecondsToTime = (
  milliseconds: string | number,
  timeZone: string = 'UTC',
  format: '12hour' | '24hour' = '12hour'
) => {
  // Parse milliseconds as number
  const ms = typeof milliseconds === "string" ? parseFloat(milliseconds) : milliseconds;
  
  // Create Date object from milliseconds
  const date = new Date(ms);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  // Configure options based on format preference
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: format === '12hour'
  };

  // Format and return the time
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

export const convertToUserTimezone = (
  utcDateTime: string,
  userTimeZone: string
) => {
  // Parse the UTC date-time
  const utcDate = DateTime.fromISO(utcDateTime, { zone: "utc" });

  // console.log("convertToUserTimeZone", utcDate);

  // Check if the parsed date is valid
  if (!utcDate.isValid) {
    throw new Error(`Invalid date format: ${utcDateTime}`);
  }

  // Convert to the user's timezone
  const userDate = utcDate.setZone(userTimeZone);

  // Format the date-time explicitly with AM/PM
  return userDate.toFormat("dd LLL yyyy, hh:mm:ss a");
};

// export function urlBase64ToUint8Array(base64String: string) {
//   const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
//   const base64 = (base64String + padding)
//     .replace(/\\-/g, "+")
//     .replace(/_/g, "/");

//   const rawData = window.atob(base64);
//   const outputArray = new Uint8Array(rawData.length);

//   for (let i = 0; i < rawData.length; ++i) {
//     outputArray[i] = rawData.charCodeAt(i);
//   }
//   return outputArray;
// }

export const defaultMenuItems = [
  {
    name: "Driver",
    path: "/driver",
    icon: QrCodeIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/driver"),
    children: [
      {
        name: "Inspection",
        path: "/driver/inspection",
      },
      {
        name: "Chat - Dispatch",
        path: "/driver/chat-dispatch",
      },
      {
        name: "Chat - HR",
        path: "/driver/chat-hr",
      },
      {
        name: "Bag Order",
        path: "/driver/bag-order",
      },
      {
        name: "My Schedule",
        path: "/driver/schedule",
      },
      {
        name: "Fleet Documents",
        path: "/driver/fleet-documents",
      },
      {
        name: "Write-ups",
        path: "/driver/write-ups",
      },
      {
        name: "Performance",
        path: "/driver/performance",
      },
      {
        name: "Compliance",
        path: "/driver/compliance",
      },
      {
        name: "Unplanned Stops",
        path: "/driver/unplanned-stops",
      }
    ],
  },
  {
    name: "Chat",
    path: "/chat",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/chat"),
    children: [
      {
        name: "Dispatch",
        path: "/chat/dispatch",
      },
      {
        name: "HR",
        path: "/chat/hr",
      },
    ],
  },
  {
    name: "Associates",
    path: "/associates",
    icon: ComputerDesktopIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/associates"),
    children: [
      {
        name: "Onboarding Applicants",
        path: "/associates/onboarding-applicants",
      },
      {
        name: "Active Employee",
        path: "/associates/employee",
      },
      {
        name: "Employee Report",
        path: "/associates/employee-report",
      },
    ],
  },
  {
    name: "Schedule",
    path: "/schedule",
    icon: ClockIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/schedule"),
    children: [
      {
        name: "Schedule Admin",
        path: "/schedule/admin",
      },
      {
        name: "Dispatch",
        path: "/schedule/dispatch",
      },
      {
        name: "Settings",
        path: "/schedule/settings",
      },
    ],
  },
  {
    name: "Operations",
    path: "/operation",
    icon: MagnifyingGlassIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/operation"),
    children: [
      {
        name: "Dashboard",
        path: "/operation/dashboard",
      },
      {
        name: "Delivery",
        path: "/operation/delivery",
      },
      {
        name: "Tips",
        path: "/operation/tips",
      },
      {
        name: "Route Observation",
        path: "/operation/route-observation",
      },
      {
        name: "Incident",
        path: "/operation/incident",
      },
      {
        name: "Clinics",
        path: "/operation/clinics",
      }
    ],
  },
  {
    name: "Asset",
    path: "/asset",
    icon: DeviceTabletIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/asset"),
    children: [
      {
        name: "Mobile",
        path: "/asset/mobile",
      },
      {
        name: "Toll Tag",
        path: "/asset/tolltag",
      },
      {
        name: "Gas Card",
        path: "/asset/gascard",
      },
    ]
  },
  {
    name: "Fleet",
    path: "/fleet-management",
    // suggest a icon which seems like van or vehicle from current library
    icon: TruckIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/fleet-management"),
    children: [
      {
        name: "Dashboard",
        path: "/fleet-management/dashboard",
      },
      {
        name: "Vehicle",
        path: "/fleet-management/vehicle",
      },
      {
        name: "Inspection",
        path: "/fleet-management/inspection",
      },
      {
        name: "Fleet Repair",
        path: "/fleet-management/fleet-repair",
      },
    ],
  },
  {
    name: "Performance",
    path: "/performance",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/performance"),
    children: [
      {
        name: "Dashboard",
        path: "/performance/dashboard",
      },
      {
        name: "Leaderboard",
        path: "/performance/leaderboard",
      },
      {
        name: "CDF",
        path: "/performance/customer-delivery-feedback-negative",
      },
      {
        name: "DSB",
        path: "/performance/delivery-success-behaviour",
      },
      {
        name: "DCR",
        path: "/performance/delivery-completion-rate",
      },
      {
        name: "Coaching",
        path: "/performance/coaching",
      },
      {
        name: "Settings",
        path: "/performance/settings",
      }
    ]
  },
  {
    name: "Payroll",
    path: "/payroll",
    icon: CurrencyDollarIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/payroll"),
    children: [
      {
        name: "Timecard Review",
        path: "/payroll/timecard-review",
      },
      {
        name: "Termination",
        path: "/payroll/termination",
      },
      {
        name: "Daily Operation",
        path: "/payroll/daily-operation",
      },
      {
        name:"Payroll ID",
        path:"/payroll/payroll-id"
      },
      {
        name:"Compliance",
        path:"/payroll/compliance"
      },
      {
        name:"Compliance Rules",
        path:"/payroll/compliance-rules"
      },
      {
        name:"Unplanned Stops",
        path:"/payroll/unplanned-stops"
      },
    ],
  },
  {
    name: "Invoice",
    path: "/invoice",
    icon: DocumentTextIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/invoice"),
    children: [
      {
        name: "Fleet",
        path: "/invoice/fleet",
      },
      {
        name: "Fleet 3P",
        path: "/invoice/fleet-3p",
      },
      {
        name: "UPD",
        path: "/invoice/upd",
      },
      {
        name: "Variable",
        path: "/invoice/variable",
      },      
    ],
  },
  {
    name: "Reports",
    path: "/reports",
    icon: ReceiptRefundIcon,
    activeCondition: (pathname: string) =>
      pathname.includes("reports"),
    children: [
      {
        name: "Attendance",
        path: "/reports/attendance",
      },
    ],
  },
  {
    name: "Settings",
    path: "/settings",
    icon: Cog8ToothIcon,
    activeCondition: (pathname: string) => 
      pathname.includes("/settings") || 
      pathname.includes("/integrations") || 
      pathname.includes("/users") || 
      pathname.includes("/subscription") || 
      pathname.includes("/organization-config") ||
      pathname.includes("/note-templates"),
    children: [
      {
        name: "Integrations",
        path: "/integrations",
      },
      {
        name: "Templates",
        path: "/note-templates",
      },
      {
        name: "Users",
        path: "/users",
      },
      {
        name: "Subscription",
        path: "/subscription",
      },
      {
        name: "Organization",
        path: "/organization-config",
      },
    ],
  },
  {
    name: "Admin",
    path: "/admin",
    icon: Cog8ToothIcon,
    activeCondition: (pathname: string) => pathname.startsWith("/admin"),
    children: [
      {
        name: "Chat",
        path: "/admin/chat",
      },
    ],
  }
];

// Define pages that should default to "All" selection
export const defaultAllPages = [
  "/fleet-management/vehicle",
];

export const driverMenuItems = [
  {
    name: "Inspection",
    path: "/driver/inspection",
    icon: MagnifyingGlassIcon,
    activeCondition: (pathname: string) => pathname.includes("inspection"),
  },
  {
    name: "Chat - Dispatch",
    path: "/driver/chat-dispatch",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.includes("chat-dispatch"),
  },
  {
    name: "Chat - HR",
    path: "/driver/chat-hr",
    icon: ChatBubbleLeftEllipsisIcon,
    activeCondition: (pathname: string) => pathname.includes("chat-hr"),
  },
  {
    name: "Bag Order",
    path: "/driver/bag-order",
    icon: EnvelopeIcon,
    activeCondition: (pathname: string) => pathname.includes("bag-order"),
  },
  {
    name: "My Schedule",
    path: "/driver/schedule",
    icon: ClockIcon,
    activeCondition: (pathname: string) => pathname.includes("schedule"),
  },
  {
    name: "Fleet Documents",
    path: "/driver/fleet-documents",
    icon: DocumentIcon,
    activeCondition: (pathname: string) => pathname.includes("fleet-documents"),
  },
  {
    name: "Write-ups",
    path: "/driver/write-ups",
    icon: PencilSquareIcon,
    activeCondition: (pathname: string) => pathname.includes("write-ups"),
  },
  {
    name: "Performance",
    path: "/driver/performance",
    icon: BoltIcon,
    activeCondition: (pathname: string) => pathname.includes("performance"),
  },
  {
    name: "Compliance",
    path: "/driver/compliance",
    icon: ExclamationTriangleIcon,
    activeCondition: (pathname: string) => pathname.includes("compliance"),
  },
  {
    name: "Unplanned Stops",
    path: "/driver/unplanned-stops",
    icon: MapPinIcon,
    activeCondition: (pathname: string) => pathname.includes("unplanned-stops"),
  }
];

/**
 * Sorts and converts a list of van objects.
 * 
 * This function takes an array of objects containing a `vehicleName` property,
 * filters out any entries with null or empty `vehicleName`, sorts the remaining
 * names numerically if possible, and then alphanumerically, and finally converts
 * them back into an array of objects.
 */

export function sortAndConvertVanList(vanList: { vehicleName: string | null, serviceType: string | null }[]): { vehicleName: string | null, serviceType: string | null }[] {
  return vanList
    .filter((van) => van.vehicleName !== null && van.vehicleName !== "") // Ensure non-null and non-empty vehicleName
    .sort((a: any, b: any) => {
      const numA = Number(a.vehicleName);
      const numB = Number(b.vehicleName);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB; // Numeric sorting
      return a.vehicleName.localeCompare(b.vehicleName, undefined, { numeric: true })
    });
}

export function generateRandomPassword(length: number = 12): string {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
}

export const formatUTCTo12Hour = (utcString: string) => {
  const date = new Date(utcString); // Convert UTC string to Date object

  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Month is 0-based
  const day = date.getUTCDate().toString().padStart(2, '0');

  // Extract UTC time components
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12; // Convert 0 to 12 for AM/PM format
  const formattedMinutes = minutes.toString().padStart(2, '0'); // Ensure 2-digit minutes

  // Format: YYYY-MM-DD HH:MM AM/PM
  return `${year}-${month}-${day} ${hours}:${formattedMinutes} ${ampm}`;
};

export interface GenerateS3FileNameParams {
  organizationId: number;
  source: string; // Source context (e.g. "fleet-management", "associates")
  identifier?: string | null; // Additional identifier like vehicle VIN or employee ID
  inspectionType?: string | null; // Type of inspection (e.g. "pre-inspection", "post-inspection", "pickup-inspection", "return-inspection")
  fieldKey: string; // Field identifier for the file
}

export async function generateS3FileName({
  organizationId,
  source,
  identifier,
  inspectionType,
  fieldKey,
}: GenerateS3FileNameParams): Promise<string> {
  const today = new Date();
  const dateString = today.toISOString().slice(0, 10).replace(/-/g, '');
  const randomChars = Math.random().toString(36).substring(2, 10);

  // Build the path components
  const pathComponents = [
    organizationId,
    source,
    ...(identifier ? [identifier] : []),
    ...(inspectionType ? [inspectionType] : []),
    dateString,
    `${fieldKey}-${randomChars}`,
  ];
  return pathComponents.join('/');
}

export const convertMillisecondsToMinutesSeconds = (
  ms: number,
  showSign: boolean = true
) => {
  const isNegative = ms < 0;
  const totalSeconds = Math.floor(Math.abs(ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const sign = showSign ? (isNegative ? '-' : '+') : '';

  const paddedSeconds = String(seconds).padStart(2, '0');
  return `${sign}${minutes}:${paddedSeconds} mins`;
};

// Convert minutes to hours and minutes format (H:MM)
export const convertMinutesToHoursMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const paddedMinutes = String(remainingMinutes).padStart(2, '0');

  return `${hours}:${paddedMinutes} hrs`;
};

// Format display name
export const formatDisplayName = (value: string): string => {
  // Convert camelCase or snake_case to Title Case With Spaces
  return value
    .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/-/g, ' ') // Replace hyphens with spaces
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

export const removeQueryParamsFromPreSignedUrl = (url: string): string => {
  const index = url.indexOf("?");
  return index === -1 ? url : url.substring(0, index);
};


export const readCSVFile = async (file: File): Promise<any[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      // First, read the file content as text to preprocess it
      const fileContent = await file.text();
      
      // Clean up the CSV content:
      // 1. Remove trailing spaces after quotes at the end of lines
      // 2. Ensure proper line endings
      // 3. Remove any trailing whitespace at the end of the file
      const cleanedContent = fileContent
        .split('\n')
        .map(line => {
          // Remove trailing spaces after quotes at the end of each line
          return line.replace(/"\s+$/g, '"').trim();
        })
        .filter(line => line.length > 0) // Remove empty lines
        .join('\n')
        .trim(); // Remove any trailing whitespace from the entire file
      
      // Parse the cleaned content directly
      Papa.parse(cleanedContent, {
        header: true,
        skipEmptyLines: 'greedy',
        quoteChar: '"',
        escapeChar: '"',
        delimiter: ',',
        transformHeader: (header) => header.trim(),
        transform: (value) => value.trim(),
        complete: (results) => {
          // Filter out only critical errors, ignore quote formatting issues if data is still parseable
          const criticalErrors = results.errors.filter(error => 
            error.code !== 'InvalidQuotes' && 
            error.code !== 'MissingQuotes' &&
            error.code !== 'TooFewFields' &&
            error.code !== 'TooManyFields'
          );
          
          if (criticalErrors.length > 0) {
            console.error('Critical CSV parsing errors:', criticalErrors);
            reject(criticalErrors[0].message);
            return;
          }
          
          // Log non-critical errors for debugging but continue processing
          if (results.errors.length > 0) {
            console.warn('Non-critical CSV parsing warnings:', results.errors);
          }
          
          console.log(`Successfully parsed CSV with ${results.data.length} rows`);
          resolve(results.data);
        },
        error: (error: any) => {
          console.error('CSV parsing error:', error);
          reject(error.message);
        }
      });
    } catch (error: any) {
      console.error('Error preprocessing CSV file:', error);
      reject(`Error preprocessing CSV file: ${error}`);
    }
  });
};

// Function to parse Excel files (XLS/XLSX)
export const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          // Parses to JSON, using headers in first row
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };


export const compressImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Only compress images
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      const MAX_WIDTH = 1920; // Max width for the image
      const MAX_HEIGHT = 1080; // Max height for the image

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      // Set canvas dimensions to the optimized size
      canvas.width = width;
      canvas.height = height;

      // Draw image onto canvas with smoothing
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob with optimized quality
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create new file from blob
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg", // Convert to JPEG for better compression
                lastModified: Date.now(),
              });

              resolve(compressedFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg", // Use JPEG format
          0.9 // 90% quality - good balance between quality and compression
        );
      } else {
        reject(new Error("Failed to get canvas context"));
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Converts seconds to hours:minutes format
 * @param seconds - Total seconds to convert
 * @returns Formatted string in "H:MM" or "HH:MM" format
 */
export function convertSecondsToHoursMinutes(seconds: number): string {
  // Handle negative values
  const absoluteSeconds = Math.abs(seconds);
  
  // Calculate hours and minutes
  const hours = Math.floor(absoluteSeconds / 3600);
  const minutes = Math.floor((absoluteSeconds % 3600) / 60);
  
  // Format with leading zero for minutes
  const formattedMinutes = minutes.toString().padStart(2, '0');
  
  // Return formatted string
  return `${hours}:${formattedMinutes} hrs`;
}

export function epochMillisecondsToYYYYMMDDdate(epochTime: number, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return formatter.format(new Date(epochTime));
}

/**
 * Fetches and stores subscription data in IndexedDB (CLIENT-SIDE ONLY)
 * @param organizationId The organization ID
 */
export const fetchAndStoreSubscriptionData = async (organizationId: string): Promise<void> => {
  try {
    if (!organizationId || typeof window === 'undefined') {
      return;
    }

    // Make API call to fetch subscription data
    const response = await fetch('/api/subscriptions/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pathname: '/' }),
    });

    if (!response || !response.ok) {
      console.error('Failed to fetch subscription data:', response?.statusText);
      return;
    }

    const data = await response.json();
    
    // Extract result from the new API response structure
    const result = data.result;
    
    if (result && result.planId && result.restrictedPages) {
      // Store subscription data in IndexedDB
      await setSubscriptionData({
        subscriptionPlanId: result.planId,
        subscriptionPlanName: result.planName,
        restrictedPages: result.restrictedPages || []
      });
    }
  } catch (error) {
    console.error("Error fetching and storing subscription data:", error);
  }
};

/**
 * Determines the appropriate redirect path based on user role
 * Used by both login and signup flows
 */
export const determineRedirectPath = async (user: any, setOrganizationName: (name: string) => void, fetchUserDetails: () => Promise<void>, isPWA: boolean) => {
  let path = '/';  
  const response:any = await fetchUserDetails();

  if(user?.organizationName){
    setOrganizationName(user.organizationName);
  }
  // Fetch subscription data and store in IndexedDB
  try {
    if (user?.organizationId) {
      await fetchAndStoreSubscriptionData(user.organizationId);
    }
  } catch (error) {
    console.error("Error fetching subscription data:", error);
  }

  if(isPWA){
  path = response?.defaultPage;
  }else{
  path = "/install";
  }

 return path;
};

/**
 * Array of path prefixes where the service area dropdown should NOT be shown
 * Add new path prefixes here to exclude them from showing the service area dropdown
 */
export const serviceAreaDropdownExcludedPaths = [
  '/driver/',      // Paths starting with /driver/
  '/settings',     // Paths starting with /settings
  '/install',      // Paths starting with /install
  '/add-organization', // Paths starting with /add-organization
  '/operation/dashboard', 
  '/operation/route-observation', 
  '/operation/tips', 
  '/operation/clinics',
  '/subscription', // Paths starting with /subscription
  '/announcement',
  '/integrations',
  '/note-templates',
  '/fleet-management/dashboard',
  '/payroll/compliance-rules',
  '/users',
  '/schedule/settings',
  '/admin/announcement',
  '/admin/chat'
];

/**
 * Only Admin Paths that are excluded from PROVIDER_LINQOPS_ADMIN role validation
 * These are system-level paths that should not require PROVIDER_LINQOPS_ADMIN role restrictions:
 * - api/admin/cron/*: API endpoint for cron jobs
 * - api/admin/webhook/*: API endpoint triggered by Amazon to add employees to the system
 */
export const adminExcludedPaths = [
  'api/admin/cron/*',
  'api/admin/webhook/*'
]

/**
 * Handles 402 Payment Required responses by validating subscription data
 * and updating IndexedDB if necessary
 * 
 * @param response The API response object (with 402 status)
 * @param organizationId The organization ID
 * @param pathname The current pathname
 * @returns Object with updated subscription access status
 */
export async function handleSubscriptionRequired(
  response: Response,
  pathname: string
): Promise<{ hasAccess: boolean; currentPlan: string | null }> {
  // Only proceed if this is a 402 Payment Required response
  if (response.status !== 402) {
    return { hasAccess: true, currentPlan: null };
  }

  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return { hasAccess: false, currentPlan: null };
    }

    // Import dynamically to avoid SSR issues
    const { getSubscriptionData, setSubscriptionData } = await import('./indexedDB');

    // Call the validation API to get the latest subscription data
    const validateResponse = await fetch('/api/subscriptions/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pathname }),
    });

    if (!validateResponse.ok) {
      console.error('Failed to validate subscription:', validateResponse.statusText);
      return { hasAccess: false, currentPlan: null };
    }

    const { result } = await validateResponse.json();
    
    // Get current subscription data from IndexedDB
    const currentData = await getSubscriptionData();
    
    // Check if the data needs to be updated
    const needsUpdate = !currentData || 
      JSON.stringify(currentData.restrictedPages) !== JSON.stringify(result.restrictedPages);
    
    // Update IndexedDB if needed
    if (needsUpdate) {
      await setSubscriptionData({
        subscriptionPlanId: result.planId,
        subscriptionPlanName: result.planName,
        restrictedPages: result.restrictedPages || [],
      });
    }

    // Return the updated access status
    return { 
      hasAccess: result.hasAccess, 
      currentPlan: result.planName 
    };
  } catch (error) {
    console.error('Error handling subscription required:', error);
    return { hasAccess: false, currentPlan: null };
  }
}

export function getWeekNumberFromEpoch(epochMs: any): number | null {
  const week = moment.utc(epochMs, "YYYY-MM-DD").isoWeek();
  return week;
}

/**
 * Week option interface for dropdown selections
 */
export interface WeekOption {
  label: string;        // "Week 1" or "2025 – Week 52"
  value: string;        // "week-1"
  startDate: string;    // "2026-01-01"
  endDate: string;      // "2026-01-07"
  weekNumber?: number;  // 1 (optional, for future use)
  year?: number;        // 2026 (optional, for future use)
}

/**
 * Generates week options for dropdown with proper ISO week-year formatting
 * 
 * Weeks belonging to the current ISO week-year display as "Week 1", "Week 2", etc.
 * Weeks belonging to previous ISO week-years display as "2025 - Week 52", "2025 - Week 51", etc.
 * 
 * Important: Uses ISO week-year logic, not calendar year. For example:
 * - Dec 29, 2025 is part of Week 1 of 2026 (ISO week-year 2026), so it displays as "Week 1"
 * - This ensures weeks are labeled based on which year they belong to according to ISO standards
 */
export function generateWeekOptions(
  todayInUserTz: Date,
  maxWeeksBack = 12
): WeekOption[] {
  const options: WeekOption[] = [];

  let cursorDate = todayInUserTz;
  
  // Get the current ISO week-year (which year the current week belongs to)
  const currentWeekYear = getWeekYear(todayInUserTz, {
    weekStartsOn: 0,
    firstWeekContainsDate: 1,
  });

  for (let i = 0; i < maxWeeksBack; i++) {
    const weekStart = startOfWeek(cursorDate, {
      weekStartsOn: 0,
    });

    const weekEnd = endOfWeek(weekStart, {
      weekStartsOn: 0,
    });

    const weekNumber = getWeek(cursorDate, {
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    });

    // Get the ISO week-year for this week (which year this week belongs to)
    const weekYear = getWeekYear(cursorDate, {
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    });

    // If the week belongs to the current ISO week-year, show "Week X"
    // Otherwise, show "YYYY - Week X" with the ISO week-year
    const label =
      weekYear === currentWeekYear
        ? `Week ${weekNumber}`
        : `Week ${weekNumber}, ${weekYear}`;

    options.push({
      label,
      value: `week-${weekYear}-${weekNumber}`,
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd'),
      weekNumber,
      year: weekYear,
    });

    cursorDate = subWeeks(cursorDate, 1);
  }

  return options;
}

/**
 * Month option interface for dropdown selections
 */
export interface MonthOption {
  label: string;        // "January" or "2025 - December"
  value: string;        // "0", "1", "2", etc. (month number as string)
  startDate: string;    // "2026-01-01"
  endDate: string;      // "2026-01-31"
  monthNumber?: number; // 0-11 (January = 0)
  year?: number;        // 2026
}

/**
 * Generates month options for dropdown with proper year formatting
 * 
 * Months belonging to the current calendar year display as "January", "February", etc.
 * Months belonging to previous calendar years display as "2025 - December", "2025 - November", etc.
 * 
 * @param todayInUserTz - Current date in user's timezone
 * @param maxMonthsBack - Number of months to go back (default: 12)
 * @returns Array of month options with labels, values, and date ranges
 */
export function generateMonthOptions(
  todayInUserTz: Date,
  maxMonthsBack = 12
): MonthOption[] {
  const options: MonthOption[] = [];
  
  // Get current year
  const currentYear = todayInUserTz.getFullYear();
  
  // Start from current month
  let cursorDate = todayInUserTz;
  
  for (let i = 0; i < maxMonthsBack; i++) {
    const monthDate = subMonths(cursorDate, i);
    
    const monthNumber = monthDate.getMonth(); // 0-11
    const year = monthDate.getFullYear();
    const monthName = format(monthDate, 'MMMM');
    
    // Calculate start and end dates
    const startDate = startOfMonth(monthDate);
    const endDate = endOfMonth(monthDate);
    
    // If the month belongs to the current year, show "January"
    // Otherwise, show "2025 - December" with the year
    const label = year === currentYear
      ? monthName
      : `${monthName}, ${year}`;
    
    options.push({
      label,
      value: monthNumber.toString(),
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      monthNumber,
      year,
    });
  }
  
  return options;
}

/**
 * Gets date range for trailing 6 weeks from today
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function getTrailing6WeeksDateRange() {
  const today = new Date();
  // Calculate 6 weeks ago (42 days)
  const sixWeeksAgo = new Date(today);
  sixWeeksAgo.setDate(today.getDate() - 42);

  // Format dates as YYYY-MM-DD
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(sixWeeksAgo),
    endDate: formatDate(today)
  };
}

export function normalizePhoneNumber(phone: string | null): string | null {
  if (!phone) return null;

  // Already in E.164 format
  if (phone.startsWith('+')) {
    return phone;
  }

  // If it's 10 digits, assume it's US and add +1
  if (/^\d{10}$/.test(phone)) {
    return `+1${phone}`;
  }

  return null; // invalid format
}

/**
 * Format phone number for tel: links
 * Removes all non-digit characters and returns plain digits with +1 prefix
 * This format works correctly with tel: links on mobile devices
 * 
 * @param phoneNumber - Raw phone number string (can be in any format)
 * @returns Formatted phone number for tel: link (e.g., +18452003703) or null if invalid
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber) return null;

  // Remove non-digits
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Match 10 digits OR 11 digits starting with 1
  if (cleaned.length === 10) return `+1${cleaned}`;
  if (cleaned.length === 11 && cleaned.startsWith("1")) return `+${cleaned}`;

  return null;
}

export function getTodayInTimezone(timezone: string, baseDate: Date = new Date()) {
  const zoned = toZonedTime(baseDate, timezone);
  return startOfDay(zoned);
}

/**
 * Given a date in 'yyyy-mm-dd' format, returns the second-to-last day of the previous month.
 * Example: '2025-01-01' → '2024-12-30'
 * @param dateString - Date in 'yyyy-mm-dd' format
 * @returns Second-to-last day of the previous month in 'yyyy-mm-dd' format
 */
export function getSecondLastDayOfPreviousMonth(dateString: string): string {
  // Parse the input date
  const inputDate = parseISO(dateString);
  
  // Get the previous month
  const previousMonth = subMonths(inputDate, 1);
  
  // Get the last day of the previous month
  const lastDay = lastDayOfMonth(previousMonth);
  
  // Get the second-to-last day (subtract 1 day)
  const secondLastDay = subDays(lastDay, 1);
  
  // Return in 'yyyy-mm-dd' format
  return format(secondLastDay, 'yyyy-MM-dd');
}

/**
 * Validates integration credentials for both Paycom and ADP
 * Uses constants from INTEGRATION_CREDENTIAL_FIELDS
 * 
 * @param integrationType - Type of integration ('paycom' or 'adp')
 * @param credentials - Credentials object
 * @param certificatePem - Certificate PEM string (for ADP only)
 * @param privateKey - Private key string (for ADP only)
 * @param requireAllFields - Whether to require all fields (default: true)
 * @returns Validation result with isValid, missingFields, and detailed validation info
 */
export function validateIntegrationCredentials(
  integrationType: string,
  credentials: Record<string, any>,
  certificatePem?: string | null,
  privateKey?: string | null,
  requireAllFields: boolean = true
): { 
  isValid: boolean; 
  missingFields: string[]; 
  isPartial: boolean;
  certValid?: boolean;
  keyValid?: boolean;
} {
  const missingFields: string[] = [];

  if (integrationType === INTEGRATIONS_TYPE.PAYCOM) {
    // Validate Paycom credentials
    const requiredFields = INTEGRATION_CREDENTIAL_FIELDS.PAYCOM.CREDENTIALS;
    requiredFields.forEach(field => {
      if (!credentials[field]) {
        missingFields.push(field);
      }
    });
  } else if (integrationType === INTEGRATIONS_TYPE.ADP) {
    // Validate ADP credentials
    const credentialFields = INTEGRATION_CREDENTIAL_FIELDS.ADP.CREDENTIALS;
    const certificateFields = INTEGRATION_CREDENTIAL_FIELDS.ADP.CERTIFICATES;
    
    // Check basic credentials
    credentialFields.forEach(field => {
      if (!credentials[field]) {
        missingFields.push(field);
      }
    });
    
    // Validate certificate format if provided
    let certValid = false;
    if (certificatePem && certificatePem.trim() !== '') {
      certValid = certificatePem.includes("BEGIN CERTIFICATE") && certificatePem.includes("END CERTIFICATE");
    }
    
    // Validate private key format if provided
    let keyValid = false;
    if (privateKey && privateKey.trim() !== '') {
      keyValid = privateKey.includes("BEGIN PRIVATE KEY") && privateKey.includes("END PRIVATE KEY");
    }
    
    // Add certificate fields to missing if required but not provided or invalid
    if (requireAllFields) {
      if (!certificatePem || !certValid) missingFields.push('certificate_pem');
      if (!privateKey || !keyValid) missingFields.push('private_key');
    }
    
    return { 
      isValid: missingFields.length === 0 && certValid && keyValid, 
      missingFields, 
      isPartial: false,
      certValid, 
      keyValid 
    };
  }
  
  return { 
    isValid: missingFields.length === 0, 
    missingFields, 
    isPartial: false 
  };
}

/**
 * Convert a string to title case (first letter capitalized, rest lowercase)
 * Example: "BUDGET" -> "Budget", "budget" -> "Budget"
 */
export const toTitleCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Normalize provider name to title case while preserving suffix
 * Example: "BUDGET - RENTAL" -> "Budget - RENTAL"
 * Only the provider part (before the dash) is converted to title case
 * Exception: "LP" provider is left unchanged
 */
export const normalizeProviderName = (providerName: string): string => {
  if (!providerName) return providerName;
  
  const parts = providerName.split(' - ');
  if (parts.length >= 2) {
    // Get the provider part
    const provider = parts[0];
    
    // If provider is "LP", leave it unchanged
    if (provider.toUpperCase() === 'LP') {
      const suffix = parts.slice(1).join(' - '); // Preserve everything after first dash
      return `${provider} - ${suffix}`;
    }
    
    // Convert other providers to title case, keep suffix as-is
    const normalizedProvider = toTitleCase(provider);
    const suffix = parts.slice(1).join(' - '); // Preserve everything after first dash
    return `${normalizedProvider} - ${suffix}`;
  }
  
  // If no dash found, check if it's "LP" (leave unchanged) or convert to title case
  if (providerName.toUpperCase() === 'LP') {
    return providerName;
  }
  
  // If no dash found, just convert to title case
  return toTitleCase(providerName);
};

/**
 * Generates Amazon Logistics Portal itinerary URL for a driver
 * 
 * @param itineraryId - The itinerary ID from itinerarySummaries table
 * @param date - The date in 'yyyy-MM-dd' format
 * @param serviceAreaId - The service area ID
 * @returns The complete Amazon portal URL with itineraryId if available, otherwise base URL
 */
export const generateAmazonItineraryUrl = (
  itineraryId: string | null | undefined,
  date: string,
  serviceAreaId: string
): string => {
  const baseUrl = `https://logistics.amazon.com/operations/execution/itineraries`;
  const queryParams = `?navMenuVariant=external&provider=ALL_DRIVERS&selectedDay=${date}&serviceAreaId=${serviceAreaId}`;

  if (!itineraryId) {
    return `${baseUrl}${queryParams}`;
  }

  return `${baseUrl}/${itineraryId}/documentType/Itinerary${queryParams}`;
};

/**
 * Checks if an employee record has null package data (packagesDelivered and flexLogOut)
 * Used for filtering employees with null data when they have exclusion notes
 * 
 * @param record The employee record to check
 * @returns boolean - true if both packagesDelivered AND flexLogOut are null
 * 
 * @example
 * // Check if employee has null package data
 * if (!hasNullData(record)) {
 *   return true; // Keep employees with valid data
 * }
 */
export function hasNullData(record: any): boolean {
  // Check specifically for the two required fields: packagesDelivered and flexLogOut
  const hasNoFlexLogOut = !record.flexLogOut || record.flexLogOut === null;
  const hasNoPackagesDelivered = !record.packagesDelivered || record.packagesDelivered === null;
  
  // Only return true if BOTH packagesDelivered AND flexLogOut are null
  // This is the specific requirement for filtering employees
  return hasNoFlexLogOut && hasNoPackagesDelivered;
}